// ============================================
// COMPREHENSIVE TRT (Testosterone Replacement Therapy) QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Comprehensive TRT Questionnaire" (2026).
// Visit Type: trt
// Lab integration: belugaOrdersLabs + labMethod (atHome | inPerson).
// Score panels: LTRS (Low Testosterone Risk Score, 0-32), qADAM (10-50).
//
// See: docs/BACKEND_TRT_LAB_INTEGRATION_PRD.md for the canonical contract.

// ============================================
// SHARED OPTION CONSTANTS
// ============================================

const YES_NO = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const QADAM_5 = [
  { label: 'Terrible', value: 'terrible', points: 1 },
  { label: 'Poor', value: 'poor', points: 2 },
  { label: 'Average', value: 'average', points: 3 },
  { label: 'Good', value: 'good', points: 4 },
  { label: 'Excellent', value: 'excellent', points: 5 },
];

// ============================================
// CONSENT BODIES (verbatim from docx)
// ============================================

const SLEEP_APNEA_CONSENT_BODY = `TRT can worsen existing sleep apnea or reveal previously undiagnosed sleep apnea. If you notice increased snoring, gasping at night, morning headaches, excessive daytime fatigue, or if someone observes breathing pauses during your sleep, please notify us immediately. Untreated sleep apnea can lead to low nighttime oxygen levels and added strain on your heart. If you already have sleep apnea and are not consistently using your CPAP or other treatment, TRT may increase your risk of complications. Please keep us informed of any symptoms so we can ensure your treatment remains safe.`;

const MAIN_CONSENT_BODY = `TRT, HCG, and SERM-Based Therapy Informed Consent

You are requesting evaluation and possible treatment for symptoms related to low testosterone. Treatment options may include:
- Testosterone Replacement Therapy (TRT)
- Human Chorionic Gonadotropin (HCG)
- Selective Estrogen Receptor Modulators (SERMs) such as clomiphene or enclomiphene
- Aromatase inhibitors (AIs) when clinically appropriate

These therapies may be used alone or in combination depending on your symptoms, laboratory results, and treatment goals (including fertility preservation).

TRT may be administered as an injection, gel, oral regimen, cream, or nasal formulation. HCG, SERMs, and AIs are oral or injectable therapies used to stimulate or regulate endogenous hormone production. All treatment is prescribed and monitored by a licensed healthcare provider.

POTENTIAL BENEFITS
Treatment may improve:
- Energy, libido, and sexual function
- Muscle mass, strength, and exercise tolerance
- Mood and overall well-being
HCG and SERMs may help stimulate natural testosterone production and support fertility in some patients.

RISKS AND SIDE EFFECTS

TRT:
- Acne, oily skin
- Fluid retention
- Mood changes
- Testicular shrinkage and reduced sperm production
- Elevated red blood cell count

HCG:
- Injection site irritation
- Headache or mood changes
- Gynecomastia
- Increased estrogen levels

Clomiphene / Enclomiphene (SERMs):
- Headache
- Mood changes
- Visual disturbances (rare but important)
- Possible liver or lipid changes

Aromatase Inhibitors (AIs):
- Joint pain
- Fatigue
- Low estrogen symptoms (e.g., mood or libido changes)
- Decreased bone density with long-term use

SERIOUS (RARE) RISKS:
- Elevated hematocrit
- Cardiovascular events (clot, stroke, heart attack)
- Worsening sleep apnea
- Liver abnormalities
- Allergic reactions

Seek emergency care for chest pain, shortness of breath, severe headache, or leg swelling.

FERTILITY CONSIDERATIONS
- TRT may suppress sperm production and lead to infertility
- HCG and SERMs may help preserve or stimulate fertility, but this is not guaranteed

COMPOUNDED MEDICATIONS
Some therapies may be compounded and not FDA-approved. These may be used when clinically appropriate; however, comparative safety and efficacy data may be limited.

CONTRAINDICATIONS
You must disclose any history of:
- Prostate or breast cancer
- Polycythemia
- Severe heart disease or recent stroke
- Untreated sleep apnea
- Allergy to any prescribed therapy

MONITORING REQUIREMENTS
Ongoing treatment requires periodic labs (typically every 3-6 months), which may include:
- CBC (hematocrit)
- Testosterone levels
- PSA (when appropriate)
- Liver function and lipids
- Estradiol (when indicated)
Failure to complete required monitoring may result in dose adjustment or discontinuation.

ALTERNATIVES
- No treatment
- Lifestyle optimization (sleep, exercise, weight loss)
- Non-hormonal therapies
- HCG or SERMs alone (fertility-focused approaches)

TRUTHFULNESS AND ACCURACY OF INFORMATION
I acknowledge that the safety and appropriateness of my treatment depend on the accuracy and completeness of the information I provide. I confirm that:
- All medical history, medications, allergies, and symptoms I have provided are true, accurate, and complete to the best of my knowledge
- I will update my provider if there are any changes to my health, medications, or symptoms
- I understand that failure to provide accurate or complete information may increase my risk of adverse outcomes and may result in discontinuation of care

ACKNOWLEDGMENT AND CONSENT
I understand:
- The risks, benefits, and alternatives of TRT, HCG, SERMs, and AIs
- No outcomes are guaranteed
- Ongoing monitoring is required
- My treatment may be modified or discontinued for safety
I consent to evaluation and treatment under medical supervision.`;

// ============================================
// BLOCKED STATES (Beluga notice 2026-07-20: TRT discontinued in these
// states per their regulatory counsel — controlled-substance telemedicine
// rules). Applies to intake AND follow-up. Only remove a state after an
// explicit Beluga go-ahead.
// ============================================

export const TRT_BLOCKED_STATES = new Set(['AL', 'AR', 'CT', 'ND', 'RI']);

export const isTrtBlockedState = (state) =>
  TRT_BLOCKED_STATES.has(String(state || '').trim().toUpperCase());

export const TRT_BLOCKED_STATE_MESSAGE =
  'Due to state regulations on prescribing testosterone via telemedicine, TRT visits are not currently available in your state. We are working to bring the program back and hope to serve you soon.';

// ============================================
// DISQUALIFYING CONDITIONS (multi-select; any selected = hard reject)
// ============================================

export const TRT_DISQUALIFYING_CONDITIONS = [
  { label: 'Pituitary or hypothalamic disease', value: 'pituitary-disease', disqualifies: true },
  { label: 'Recent peripheral vision loss without a known cause', value: 'peripheral-vision-loss', disqualifies: true },
  { label: "I'm taking oral ketoconazole", value: 'ketoconazole', disqualifies: true },
  { label: "I'm taking long term glucocorticoids such as prednisone", value: 'glucocorticoids', disqualifies: true },
  { label: "I'm taking spironolactone", value: 'spironolactone', disqualifies: true },
  { label: 'History of prostate cancer and/or elevated PSA', value: 'prostate-cancer', disqualifies: true },
  { label: 'History of breast cancer', value: 'breast-cancer', disqualifies: true },
  { label: 'Polycythemia (elevated blood counts)', value: 'polycythemia', disqualifies: true },
  { label: 'Untreated or severe sleep apnea', value: 'untreated-sleep-apnea', disqualifies: true },
  { label: 'Advanced kidney disease and/or liver cirrhosis', value: 'kidney-liver-disease', disqualifies: true },
  { label: 'History of blood clots (DVT or PE)', value: 'blood-clots', disqualifies: true },
  { label: 'Uncontrolled heart failure (hospitalization within the last year for this)', value: 'uncontrolled-heart-failure', disqualifies: true },
  { label: 'Heart attack or stroke within the last 12 months', value: 'recent-mi-stroke', disqualifies: true },
  { label: 'None of these apply to me', value: 'none' },
];

// ============================================
// QUESTIONS
// ============================================

// Program intro / preamble — verbatim from Beluga's TRT source-of-truth
// (2026-06-02 review of visit 11120290). Rendered as an info-text slide and
// recorded into the visit so the provider sees the program description.
const introQuestion = {
  id: 'trt-intro',
  type: 'info-text',
  question: 'Welcome to the TRT Program',
  infoText:
    'Thank you for your interest in our program. This intake helps your provider evaluate your symptoms, history, and goals to determine safe treatment. You’ll answer questions about your health, medications, prior treatments, and fertility goals. You may also be asked for labs, photos, or medication details. Your information will be reviewed by a licensed provider, and additional details may be requested if needed.',
};

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  subtitle: 'This intake is only for individuals assigned male at birth.',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female', disqualifies: true },
  ],
};

const genderIdentityQuestion = {
  id: 'gender-identity',
  type: 'single-select',
  question: 'What is your current gender identity?',
  options: [
    { label: 'Man', value: 'man' },
    { label: 'Woman', value: 'woman' },
    { label: 'Transgender male', value: 'transgender-male' },
    { label: 'Transgender woman', value: 'transgender-woman' },
    { label: 'Non-binary', value: 'non-binary' },
  ],
};

const fertilityPlanningQuestion = {
  id: 'fertility-planning',
  type: 'single-select',
  question: 'Which of the following best describes your situation?',
  subtitle: 'Testosterone replacement therapy (TRT) can significantly reduce sperm production and may negatively impact fertility. If you are actively trying to conceive, TRT may not be the most appropriate option.',
  options: [
    { label: "I understand the risk and I'm not planning on having children and would like to continue with treatment", value: 'understand-and-continue' },
    { label: 'I am planning on having children and would be interested in alternatives such as enclomiphene', value: 'alternatives' },
    { label: 'I AM planning on having children soon and would like to cancel', value: 'cancel', disqualifies: true },
  ],
};

const disqualifyingConditionsQuestion = {
  id: 'disqualifying-conditions',
  type: 'multi-select',
  question: 'Do any of these apply to you?',
  subtitle: 'Select all that apply.',
  options: TRT_DISQUALIFYING_CONDITIONS,
};

// ---------- LTRS panel ----------

const ageRangeQuestion = {
  id: 'age-range',
  type: 'single-select',
  question: 'Select your appropriate age range',
  options: [
    { label: 'Under 40', value: 'under-40', points: 0 },
    { label: '40 or older', value: '40-or-older', points: 2 },
  ],
};

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'We use BMI as part of your risk score and to help your provider personalize care.',
  // BMI is part of LTRS; we still want the BMI card visible but never use it
  // as a disqualifier — handled by the LTRS rubric, not a hard gate.
  skipBmiCheck: true,
};

const abdominalWeightQuestion = {
  id: 'abdominal-weight',
  type: 'single-select',
  question: 'Do you carry most of your weight in your abdomen (belly fat)?',
  options: [
    { label: 'Yes', value: 'yes', points: 3 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const type2DiabetesQuestion = {
  id: 'type-2-diabetes',
  type: 'single-select',
  question: 'Have you been diagnosed with Type 2 Diabetes?',
  options: [
    { label: 'Yes', value: 'yes', points: 3 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const prediabetesQuestion = {
  id: 'prediabetes',
  type: 'single-select',
  question: 'Have you been told you have prediabetes or insulin resistance?',
  options: [
    { label: 'Yes', value: 'yes', points: 2 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const highCholesterolQuestion = {
  id: 'high-cholesterol',
  type: 'single-select',
  question: 'Have you been diagnosed with high cholesterol or abnormal lipids?',
  options: [
    { label: 'Yes', value: 'yes', points: 2 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const metabolicSyndromeQuestion = {
  id: 'metabolic-syndrome',
  type: 'single-select',
  question: 'Have you been diagnosed with metabolic syndrome?',
  options: [
    { label: 'Yes', value: 'yes', points: 3 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const obstructiveSleepApneaQuestion = {
  id: 'obstructive-sleep-apnea',
  type: 'single-select',
  question: 'Have you been diagnosed with obstructive sleep apnea?',
  options: [
    { label: 'Yes', value: 'yes', points: 3 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

// Conditional: only when obstructive-sleep-apnea = yes
const sleepApneaConsentQuestion = {
  id: 'sleep-apnea-consent',
  type: 'consent-dropdown',
  question: 'Sleep Apnea Acknowledgment',
  consentText: SLEEP_APNEA_CONSENT_BODY,
  options: [
    { label: 'I have read and understand this information and I wish to proceed', value: 'consent' },
    { label: 'I have read and understand this information and I do NOT wish to proceed', value: 'decline', disqualifies: true },
  ],
};

const osaSymptomsQuestion = {
  id: 'osa-symptoms',
  type: 'single-select',
  question: 'Do you snore loudly, feel excessively tired during the day, or has anyone noticed pauses in your breathing during sleep?',
  options: [
    { label: 'Yes', value: 'yes', points: 2 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const sleepHoursQuestion = {
  id: 'sleep-hours',
  type: 'single-select',
  question: 'On average, how many hours of sleep do you get per night?',
  options: [
    { label: 'Less than 6 hours', value: 'less-than-6', points: 2 },
    { label: '6-7 hours', value: '6-to-7', points: 1 },
    { label: '7 or more hours', value: '7-or-more', points: 0 },
  ],
};

const sleepQualityQuestion = {
  id: 'sleep-quality',
  type: 'single-select',
  question: 'How would you rate your sleep quality?',
  options: [
    { label: 'Poor (frequent awakenings, non-restorative)', value: 'poor', points: 2 },
    { label: 'Fair', value: 'fair', points: 1 },
    { label: 'Good', value: 'good', points: 0 },
  ],
};

const nightShiftsQuestion = {
  id: 'night-shifts',
  type: 'single-select',
  question: 'Do you work night shifts or rotating shifts?',
  options: [
    { label: 'Yes', value: 'yes', points: 2 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

const exerciseFrequencyQuestion = {
  id: 'exercise-frequency',
  type: 'single-select',
  question: 'How often do you exercise?',
  options: [
    { label: 'Rarely or never', value: 'rarely-or-never', points: 1 },
    { label: '1-2 times per week', value: '1-to-2-per-week', points: 1 },
    { label: '3 or more times per week', value: '3-or-more-per-week', points: 0 },
  ],
};

const dietQualityQuestion = {
  id: 'diet-quality',
  type: 'single-select',
  question: 'How would you describe your diet?',
  options: [
    { label: 'Poor (processed foods, high sugar)', value: 'poor', points: 1 },
    { label: 'Average or healthy', value: 'average-or-healthy', points: 0 },
  ],
};

const chronicStressQuestion = {
  id: 'chronic-stress',
  type: 'single-select',
  question: 'Do you experience chronic stress?',
  options: [
    { label: 'Yes', value: 'yes', points: 1 },
    { label: 'No', value: 'no', points: 0 },
  ],
};

// ---------- qADAM panel ----------

const qadamLibidoQuestion = {
  id: 'qadam-libido',
  type: 'single-select',
  question: 'How would you rate your libido (sex drive)?',
  options: QADAM_5,
};

const qadamEnergyQuestion = {
  id: 'qadam-energy',
  type: 'single-select',
  question: 'How would you rate your energy level?',
  options: QADAM_5,
};

const qadamStrengthEnduranceQuestion = {
  id: 'qadam-strength-endurance',
  type: 'single-select',
  question: 'How would you rate your strength or endurance?',
  options: QADAM_5,
};

const qadamEnjoymentQuestion = {
  id: 'qadam-enjoyment',
  type: 'single-select',
  question: 'How would you rate your enjoyment of life?',
  options: QADAM_5,
};

const qadamHappinessQuestion = {
  id: 'qadam-happiness',
  type: 'single-select',
  question: 'How would you rate your overall happiness or mood?',
  options: QADAM_5,
};

const qadamErectionsQuestion = {
  id: 'qadam-erections',
  type: 'single-select',
  question: 'How strong are your erections?',
  options: [
    { label: 'Extremely weak', value: 'extremely-weak', points: 1 },
    { label: 'Weak', value: 'weak', points: 2 },
    { label: 'Moderate', value: 'moderate', points: 3 },
    { label: 'Strong', value: 'strong', points: 4 },
    { label: 'Extremely strong', value: 'extremely-strong', points: 5 },
  ],
};

const qadamWorkPerformanceQuestion = {
  id: 'qadam-work-performance',
  type: 'single-select',
  question: 'How would you rate your work performance over the past 4 weeks?',
  options: QADAM_5,
};

const qadamFallAsleepAfterDinnerQuestion = {
  id: 'qadam-fall-asleep-after-dinner',
  type: 'single-select',
  question: 'How often do you fall asleep after dinner?',
  options: [
    { label: 'Never', value: 'never', points: 1 },
    { label: '1-2 times per week', value: '1-to-2', points: 2 },
    { label: '3-4 times per week', value: '3-to-4', points: 3 },
    { label: '5-6 times per week', value: '5-to-6', points: 4 },
    { label: 'Every night', value: 'every-night', points: 5 },
  ],
};

const qadamSportsPerformanceQuestion = {
  id: 'qadam-sports-performance',
  type: 'single-select',
  question: 'How would you rate your sports or physical performance over the past 4 weeks?',
  options: QADAM_5,
};

const qadamHeightLossQuestion = {
  id: 'qadam-height-loss',
  type: 'single-select',
  question: 'How much height have you lost?',
  options: [
    { label: '2 inches or more', value: '2-or-more', points: 1 },
    { label: '1.5-1.9 inches', value: '1.5-to-1.9', points: 2 },
    { label: '1-1.4 inches', value: '1-to-1.4', points: 3 },
    { label: '0.5-0.9 inches', value: '0.5-to-0.9', points: 4 },
    { label: 'None (0-0.4 inches)', value: 'none', points: 5 },
  ],
};

// ---------- New vs Continuation branch ----------

const newVsContinuationQuestion = {
  id: 'new-vs-continuation',
  type: 'single-select',
  question: 'Please select the option that best describes you',
  options: [
    { label: 'I do NOT have a known diagnosis of low testosterone', value: 'new' },
    { label: 'I have a known diagnosis of low testosterone and I AM currently or have previously been on testosterone replacement therapy', value: 'continuation' },
  ],
};

// New patient sub-branch
const symptomDurationQuestion = {
  id: 'symptom-duration',
  type: 'single-select',
  question: 'How long have you been experiencing symptoms that you feel may be related to low testosterone?',
  options: [
    { label: 'It has been around 1-6 months', value: '1-to-6-months' },
    { label: 'It has been 7-12 months', value: '7-to-12-months' },
    { label: 'It has been over a year', value: 'over-a-year' },
  ],
};

const lifestyleChangesQuestion = {
  id: 'lifestyle-changes',
  type: 'single-select',
  question: 'Have you made consistent efforts to improve your symptoms through lifestyle changes for at least 3 months without meaningful improvement in your symptoms?',
  options: [
    { label: 'Yes — I have made consistent lifestyle changes, but my symptoms have not improved or continue to limit my ability to maintain these changes', value: 'yes-no-improvement' },
    { label: 'Yes — I have made consistent lifestyle changes with some improvement, but I still have significant symptoms', value: 'yes-some-improvement' },
    { label: 'I have tried lifestyle changes, but have not been able to maintain them consistently due to my symptoms', value: 'tried-not-maintained' },
    { label: 'No — I have not yet made meaningful lifestyle changes', value: 'no' },
  ],
};

// Continuation patient sub-branch
const diagnosisDurationQuestion = {
  id: 'diagnosis-duration',
  type: 'single-select',
  question: 'When were you first diagnosed with low testosterone?',
  options: [
    { label: 'It has been less than 1 year', value: 'less-than-1-year' },
    { label: 'It has been 1-5 years', value: '1-to-5-years' },
    { label: 'It has been longer than 6 years now', value: 'longer-than-6-years' },
  ],
};

const currentMedicationQuestion = {
  id: 'current-medication',
  type: 'textarea',
  question: 'What medication and dose are you CURRENTLY using for testosterone replacement?',
  subtitle: 'Please specify the formulation (e.g., injection, gel, or spray) and include the full concentration and amount used per dose. Example: Testosterone cypionate 200 mg/mL, injecting 0.25 mL per dose. (If you have been off of treatment, type NONE)',
  placeholder: 'e.g., Testosterone cypionate 200 mg/mL, injecting 0.25 mL per dose',
};

const lastDoseDateQuestion = {
  id: 'last-dose-date',
  type: 'textarea',
  question: 'Please enter the exact date you last administered your testosterone dose',
  subtitle: 'Accurate timing helps us safely determine your next scheduled dose.',
  placeholder: 'e.g., 2026-05-10',
};

const medicationDurationQuestion = {
  id: 'medication-duration',
  type: 'single-select',
  question: 'How long have you been on your current medication?',
  options: [
    { label: 'Less than 3 months', value: 'less-than-3-months' },
    { label: '3 – 6 months', value: '3-to-6-months' },
    { label: 'More than 6 months', value: 'more-than-6-months' },
  ],
};

const medicationResponseQuestion = {
  id: 'medication-response',
  type: 'single-select',
  question: "Are you happy with how you're responding to this medication?",
  options: YES_NO,
};

const medicationResponseDetailsQuestion = {
  id: 'medication-response-details',
  type: 'textarea',
  question: "Tell us more about what you're experiencing",
  placeholder: 'Describe your symptoms or concerns…',
};

const trtSideEffectsQuestion = {
  id: 'trt-side-effects',
  type: 'multi-select',
  question: 'Are you experiencing any of the following since starting or adjusting TRT?',
  subtitle: 'Select all that apply.',
  options: [
    { label: 'Increased hematocrit / polycythemia', value: 'polycythemia' },
    { label: 'A provider has suggested I donate blood to reduce my blood count', value: 'donate-blood' },
    { label: 'Mood swings or depression', value: 'mood-swings' },
    { label: 'Acne or oily skin', value: 'acne' },
    { label: 'Worsening sleep apnea', value: 'worsening-sleep-apnea' },
    { label: 'Injection-site discomfort', value: 'injection-site' },
    { label: 'No notable side effects', value: 'none' },
    { label: 'I have experienced other side effects', value: 'other' },
  ],
};

const trtSideEffectsDetailsQuestion = {
  id: 'trt-side-effects-details',
  type: 'textarea',
  question: 'Tell us more about your other side effects',
  placeholder: 'Describe what you have been experiencing…',
};

const priorTrtIssuesQuestion = {
  id: 'prior-trt-issues',
  type: 'single-select',
  question: 'Have you ever had side effects or complications to other TRT products?',
  options: [
    { label: 'Yes, I have had other issues', value: 'yes' },
    { label: "No, I haven't had any other issues", value: 'no' },
  ],
};

const priorTrtIssuesDetailsQuestion = {
  id: 'prior-trt-issues-details',
  type: 'textarea',
  question: 'Tell us more about what you have experienced',
  subtitle: 'Include the reaction and which medication caused this reaction.',
  placeholder: 'Describe the reaction and medication…',
};

const doseAdjustmentPreferenceQuestion = {
  id: 'dose-adjustment-preference',
  type: 'single-select',
  question: 'Would you like to adjust your dose or formulation?',
  options: [
    { label: "No, I'm satisfied with my current therapy", value: 'no-satisfied' },
    { label: "Yes, I'm concerned about side effects", value: 'yes-side-effects' },
    { label: "Yes, I'm seeking improved symptom control", value: 'yes-symptom-control' },
    { label: "I'm unsure, please advise", value: 'unsure' },
  ],
};

// ---------- Lab routing ----------

const labAvailabilityQuestion = {
  id: 'lab-availability',
  type: 'single-select',
  question: 'Do you have testosterone lab images from the past 4 months?',
  options: [
    { label: 'Yes I have labs to provide', value: 'yes' },
    { label: 'No I need to obtain a new set of labs', value: 'no' },
  ],
};

const labsTooHighQuestion = {
  id: 'labs-too-high',
  type: 'single-select',
  question: "If you're on treatment, do your labs show that your testosterone was too high?",
  options: [
    { label: 'Yes, my testosterone value has been too high', value: 'yes' },
    { label: 'No, my testosterone value was low or within my expected range', value: 'no' },
    { label: "I don't know the results", value: 'unknown' },
  ],
};

const labImageUploadQuestion = {
  id: 'lab-image-upload',
  type: 'file-upload',
  question: 'Please upload an image of your most recent lab results',
  subtitle: 'Your uploaded image will remain private and will only be used by the physician for clinical assessment. If you have multiple pages, combine them into a single PDF before uploading.',
};

const labMethodQuestion = {
  id: 'lab-method',
  type: 'single-select',
  question: 'How would you like to complete your lab work?',
  subtitle: 'At-home: we ship a kit to you. In-person: bring a requisition PDF to a Quest or LabCorp draw site.',
  options: [
    { label: 'At-home collection kit (we ship to you)', value: 'atHome' },
    { label: 'In-person draw at a lab (Quest / LabCorp)', value: 'inPerson' },
  ],
};

// ---------- PURPLE named-field slides ----------

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Do you have any other medical conditions not mentioned?',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues.',
  placeholder: 'e.g., Hypertension, Asthma, Previous knee surgery, etc.',
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea',
  question: 'Please list all your current prescription medications, supplements, or over-the-counter medications including dosages',
  placeholder: 'e.g., Lisinopril 10mg daily, Vitamin D 2000 IU daily…',
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions.',
  placeholder: 'Type your questions or concerns here…',
};

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment.",
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address',
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle:
    "Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible. If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card. Make sure the photo is clear and legible.",
  // Required by Beluga — ID photo cannot be skipped (2026-06-27).
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'TRT, HCG, and SERM-Based Therapy Informed Consent',
  consentText: MAIN_CONSENT_BODY,
  options: [
    { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true },
  ],
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Select Your Treatment Plan',
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete',
};

// ============================================
// FULL FLOW (canonical docx order)
// ============================================

export const trtFlow = [
  introQuestion,
  sexAtBirthQuestion,
  genderIdentityQuestion,
  fertilityPlanningQuestion,
  disqualifyingConditionsQuestion,

  // LTRS panel
  ageRangeQuestion,
  heightWeightQuestion,
  abdominalWeightQuestion,
  type2DiabetesQuestion,
  prediabetesQuestion,
  highCholesterolQuestion,
  metabolicSyndromeQuestion,
  obstructiveSleepApneaQuestion,
  sleepApneaConsentQuestion, // conditional (OSA = yes)
  osaSymptomsQuestion,
  sleepHoursQuestion,
  sleepQualityQuestion,
  nightShiftsQuestion,
  exerciseFrequencyQuestion,
  dietQualityQuestion,
  chronicStressQuestion,

  // qADAM panel
  qadamLibidoQuestion,
  qadamEnergyQuestion,
  qadamStrengthEnduranceQuestion,
  qadamEnjoymentQuestion,
  qadamHappinessQuestion,
  qadamErectionsQuestion,
  qadamWorkPerformanceQuestion,
  qadamFallAsleepAfterDinnerQuestion,
  qadamSportsPerformanceQuestion,
  qadamHeightLossQuestion,

  // Branch decision
  newVsContinuationQuestion,

  // NEW patient slides (filtered out for continuation)
  symptomDurationQuestion,
  lifestyleChangesQuestion,

  // CONTINUATION patient slides (filtered out for new)
  diagnosisDurationQuestion,
  currentMedicationQuestion,
  lastDoseDateQuestion,
  medicationDurationQuestion,
  medicationResponseQuestion,
  medicationResponseDetailsQuestion,
  trtSideEffectsQuestion,
  trtSideEffectsDetailsQuestion,
  priorTrtIssuesQuestion,
  priorTrtIssuesDetailsQuestion,
  doseAdjustmentPreferenceQuestion,

  // PURPLE / named-field
  medicalConditionsQuestion,
  selfReportedMedsQuestion,
  allergiesQuestion,

  // Contact / shipping (shipping needed before lab-method for NY/NJ gate)
  basicContactQuestion,
  shippingAddressStep,

  // Lab routing
  labAvailabilityQuestion,
  labsTooHighQuestion,        // conditional
  labImageUploadQuestion,     // conditional
  labMethodQuestion,          // conditional

  additionalQuestionsQuestion,
  identityVerificationStep,
  mainConsentQuestion,
  checkoutStep,
  completeStep,
];

// ============================================
// Slide IDs grouped for scoring helpers
// ============================================

export const TRT_LTRS_SLIDE_IDS = [
  'age-range',
  // height-weight handled via BMI band derivation
  'abdominal-weight',
  'type-2-diabetes',
  'prediabetes',
  'high-cholesterol',
  'metabolic-syndrome',
  'obstructive-sleep-apnea',
  'osa-symptoms',
  'sleep-hours',
  'sleep-quality',
  'night-shifts',
  'exercise-frequency',
  'diet-quality',
  'chronic-stress',
];

export const TRT_QADAM_SLIDE_IDS = [
  'qadam-libido',
  'qadam-energy',
  'qadam-strength-endurance',
  'qadam-enjoyment',
  'qadam-happiness',
  'qadam-erections',
  'qadam-work-performance',
  'qadam-fall-asleep-after-dinner',
  'qadam-sports-performance',
  'qadam-height-loss',
];

// Index for scoring resolution: slide-id → option point lookup
export const trtFlowById = trtFlow.reduce((acc, slide) => {
  acc[slide.id] = slide;
  return acc;
}, {});

// ============================================
// Dynamic flow builder — strips slides whose conditional has not fired
// ============================================

export const buildDynamicTrtFlow = (answers) => {
  let flow = [...trtFlow];

  // Sleep-apnea consent only when OSA = yes
  if (answers['obstructive-sleep-apnea'] !== 'yes') {
    flow = flow.filter((q) => q.id !== 'sleep-apnea-consent');
  }

  // Branch on new-vs-continuation
  const branch = answers['new-vs-continuation'];
  if (branch === 'new') {
    // Drop continuation-only slides
    flow = flow.filter(
      (q) =>
        ![
          'diagnosis-duration',
          'current-medication',
          'last-dose-date',
          'medication-duration',
          'medication-response',
          'medication-response-details',
          'trt-side-effects',
          'trt-side-effects-details',
          'prior-trt-issues',
          'prior-trt-issues-details',
          'dose-adjustment-preference',
        ].includes(q.id),
    );
  } else if (branch === 'continuation') {
    // Drop new-only slides
    flow = flow.filter((q) => !['symptom-duration', 'lifestyle-changes'].includes(q.id));
  } else {
    // Branch not chosen yet — hide both downstream sub-branches
    flow = flow.filter(
      (q) =>
        ![
          'symptom-duration',
          'lifestyle-changes',
          'diagnosis-duration',
          'current-medication',
          'last-dose-date',
          'medication-duration',
          'medication-response',
          'medication-response-details',
          'trt-side-effects',
          'trt-side-effects-details',
          'prior-trt-issues',
          'prior-trt-issues-details',
          'dose-adjustment-preference',
        ].includes(q.id),
    );
  }

  // medication-response-details — only when medication-response = no
  if (answers['medication-response'] !== 'no') {
    flow = flow.filter((q) => q.id !== 'medication-response-details');
  }

  // trt-side-effects-details — only when "other" is selected
  const tse = answers['trt-side-effects'] || [];
  if (!Array.isArray(tse) || !tse.includes('other')) {
    flow = flow.filter((q) => q.id !== 'trt-side-effects-details');
  }

  // prior-trt-issues-details — only when prior-trt-issues = yes
  if (answers['prior-trt-issues'] !== 'yes') {
    flow = flow.filter((q) => q.id !== 'prior-trt-issues-details');
  }

  // Lab routing conditionals.
  // lab-method ("how would you like to complete your lab work?") is NOT part of
  // Beluga's TRT questionnaire in ANY case (Erika review of 19033161 + 44850118
  // — "not part of the questionnaire and should be removed"). It never appears in
  // the Q/A. Lab fulfilment is driven by the belugaOrdersLabs / labMethod envelope
  // fields (BE defaults the collection method), not a patient-facing slide.
  const labAvail = answers['lab-availability'];
  flow = flow.filter((q) => q.id !== 'lab-method');
  if (labAvail === 'yes') {
    // Patient has labs to upload; labs-too-high is asked only of continuation patients.
    if (branch !== 'continuation') {
      flow = flow.filter((q) => q.id !== 'labs-too-high');
    }
  } else {
    // New patient ('no') or unanswered → hide labs-too-high + lab-image-upload.
    flow = flow.filter((q) => !['labs-too-high', 'lab-image-upload'].includes(q.id));
  }

  return flow;
};

// ============================================
// Per-answer disqualifier resolver
// ============================================

export const isDisqualifyingAnswer = (questionId, answer) => {
  if (questionId === 'sex-at-birth' && answer === 'female') {
    return {
      disqualified: true,
      reason: 'This intake is only available for individuals assigned male at birth.',
    };
  }

  if (questionId === 'fertility-planning' && answer === 'cancel') {
    return {
      disqualified: true,
      reason:
        "Thanks for letting us know — we won't proceed with this submission and your card will not be charged.",
    };
  }

  // Multi-select disqualifying-conditions — any selected disqualifier triggers reject
  if (questionId === 'disqualifying-conditions' && Array.isArray(answer)) {
    const selectedDisqualifiers = TRT_DISQUALIFYING_CONDITIONS.filter(
      (opt) => opt.disqualifies && answer.includes(opt.value),
    );
    if (selectedDisqualifiers.length > 0) {
      const items = selectedDisqualifiers.map((o) => o.label).join('; ');
      return {
        disqualified: true,
        reason: `Based on your medical history (${items}), TRT is not safe for you right now. Please discuss alternatives with your primary care provider.`,
      };
    }
  }

  if (questionId === 'sleep-apnea-consent' && answer === 'decline') {
    return {
      disqualified: true,
      reason: 'Sleep-apnea acknowledgment is required for TRT. We cannot proceed.',
    };
  }

  if (questionId === 'main-consent' && answer === 'decline') {
    return {
      disqualified: true,
      reason: 'You have indicated that you do not wish to continue with this treatment.',
    };
  }

  return { disqualified: false };
};

// ============================================
// Age helper (server enforces; FE blocks early)
// ============================================

export const computeAgeFromDob = (dob) => {
  if (!dob) return null;
  // Accept MM/DD/YYYY or ISO
  let parts;
  if (dob.includes('/')) {
    const [m, d, y] = dob.split('/').map((n) => parseInt(n, 10));
    if (!m || !d || !y) return null;
    parts = new Date(y, m - 1, d);
  } else {
    parts = new Date(dob);
  }
  if (Number.isNaN(parts.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - parts.getFullYear();
  const mDiff = today.getMonth() - parts.getMonth();
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < parts.getDate())) age -= 1;
  return age;
};

// ============================================
// TRT TREATMENT PLANS
// ============================================
//
// Pricing is placeholder — backend has the canonical list seeded via
// `scripts/seed-trt-client-plans.js`. The FE may later fetch via
// `GET /api/client-plans?type=trt`. For v1 we hardcode so the checkout
// step renders without an extra round-trip; backend reconciles to the
// authoritative SKU + medId on submission anyway.

export const trtPlans = [
  {
    category: 'Testosterone Cypionate (Injectable)',
    description: 'Most common starting therapy — weekly subcutaneous injection',
    plans: [
      {
        plan_slug: 'trt-testosterone-cypionate-100-monthly',
        plan_name: 'Testosterone Cypionate 100mg/wk — Monthly',
        plan_price: 99,
        plan_content:
          'Compounded testosterone cypionate 200mg/mL,Weekly subcutaneous injections,Monthly supply,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'trt-testosterone-cypionate-100-quarterly',
        plan_name: 'Testosterone Cypionate 100mg/wk — Quarterly',
        plan_price: 249,
        plan_content:
          'Best Value - Save 17%,Compounded testosterone cypionate 200mg/mL,3-month supply,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'trt-testosterone-cypionate-150-monthly',
        plan_name: 'Testosterone Cypionate 150mg/wk — Monthly',
        plan_price: 119,
        plan_content:
          'Compounded testosterone cypionate 200mg/mL,Weekly subcutaneous injections,Monthly supply,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'trt-testosterone-cypionate-200-monthly',
        plan_name: 'Testosterone Cypionate 200mg/wk — Monthly',
        plan_price: 149,
        plan_content:
          'Compounded testosterone cypionate 200mg/mL,Weekly subcutaneous injections,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Testosterone Cream',
    description: 'For patients who prefer topical formulations',
    plans: [
      {
        plan_slug: 'trt-testosterone-cream-50-monthly',
        plan_name: 'Testosterone Cream 50mg/day — Monthly',
        plan_price: 99,
        plan_content:
          'Compounded testosterone cream,Daily topical application,Monthly supply,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'trt-testosterone-cream-100-monthly',
        plan_name: 'Testosterone Cream 100mg/day — Monthly',
        plan_price: 129,
        plan_content:
          'Compounded testosterone cream,Daily topical application,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Testosterone Topical Spray',
    description: 'Spray formulation for daily use',
    plans: [
      {
        plan_slug: 'trt-testosterone-topical-spray-20-monthly',
        plan_name: 'Testosterone Topical Spray 20mg/day — Monthly',
        plan_price: 119,
        plan_content:
          'Compounded testosterone topical spray,Daily application,Monthly supply,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Enclomiphene (SERM)',
    description: 'Fertility-preserving alternative to TRT',
    plans: [
      {
        plan_slug: 'trt-enclomiphene-12-monthly',
        plan_name: 'Enclomiphene 12.5mg — Monthly',
        plan_price: 89,
        plan_content:
          'Compounded enclomiphene,Oral daily dose,Monthly supply,Fertility-preserving,Provider consultation,Free shipping',
      },
      {
        plan_slug: 'trt-enclomiphene-25-monthly',
        plan_name: 'Enclomiphene 25mg — Monthly',
        plan_price: 99,
        plan_content:
          'Compounded enclomiphene,Oral daily dose,Monthly supply,Fertility-preserving,Provider consultation,Free shipping',
      },
    ],
  },
  {
    category: 'Anastrozole (Aromatase Inhibitor)',
    description: 'Adjuvant — only when clinically indicated',
    plans: [
      {
        plan_slug: 'trt-anastrozole-1-quarterly',
        plan_name: 'Anastrozole 1mg — Quarterly',
        plan_price: 49,
        plan_content:
          'Compounded anastrozole,Oral weekly dose,Quarterly supply,Estradiol management,Provider consultation,Free shipping',
      },
    ],
  },
];

// Plans that count as fertility-preserving / age-restricted alternatives.
// When fertility-planning === 'alternatives' OR age ∈ [25,29], the checkout
// step should only render plans whose slug is in this set.
export const TRT_FERTILITY_ALTERNATIVE_PLAN_SLUGS = new Set([
  'trt-enclomiphene-12-monthly',
  'trt-enclomiphene-25-monthly',
]);

// Fertility routing (PRD §6.2 + §11.3): patients who chose the
// fertility-preserving alternatives OR are aged 25-29 should be steered to
// enclomiphene/HCG instead of testosterone.
export function isTrtFertilityRouted(answers) {
  if (answers?.['fertility-planning'] === 'alternatives') return true;
  const age = computeAgeFromDob(answers?.['basic-contact']?.dob);
  return age !== null && age >= 25 && age <= 29;
}

// Catalog SKUs that qualify as fertility-preserving alternatives. Dark until
// the BE seeds them (docs/BACKEND_TRT_CATALOG_SEED_PRD.md) — the catalog
// checkout falls back to the full list when this filter matches nothing.
export const TRT_FERTILITY_SKU_RE = /enclomiphene|\bhcg\b|gonadotropin/i;

// Default recommendation for the catalog-driven TRT checkout
// (TrtForm → CatalogCheckoutStep recommendPick). Preference order:
//   1. fertility-routed patients → cheapest enclomiphene/HCG SKU when the
//      catalog carries one (BE seed pending; no-op until then);
//   2. Testosterone Cypionate 200mg/mL — the standard starting protocol
//      (without this the component defaults to the cheapest SKU, which is
//      currently anastrozole — an adjuvant, not a treatment);
//   3. any testosterone SKU; else null → component default (cheapest).
// NOTE: the `medication` field is NOT used for matching — beluga writes
// "Testosterone Cypionate Injection Solution" there on creams and sprays too,
// which would misclassify the whole catalog as cypionate (same trap as the
// WL catalog's medication field, see src/lib/wlAdditives.js).
export function pickTrtRecommendedSku(items, answers) {
  if (!Array.isArray(items) || !items.length) return null;
  const cheapest = (list) =>
    list.slice().sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))[0] || null;
  if (isTrtFertilityRouted(answers)) {
    const alt = cheapest(items.filter((m) => TRT_FERTILITY_SKU_RE.test(m.name || '')));
    if (alt) return alt;
  }
  return (
    cheapest(items.filter((m) => /testosterone\s+cypionate/i.test(m.name || '') && /200\s*mg/i.test(`${m.name || ''} ${m.strength || ''}`))) ||
    cheapest(items.filter((m) => /testosterone/i.test(m.name || '')))
  );
}

export default trtFlow;
